import { FieldType, FilterType, ParticipantFieldType } from '@app/api';
import React from 'react';
import { FilterData } from './useParticipationFilters';
import { RangeFilterButton } from './RangeFilterButton';
import { ChoiceValue, FieldFilterButton } from './FieldFilterButton';
import { TextSearchFilterButton } from './TextSearchFilterButton';

export const NULL_VALUE = '__NULL__';

interface Props {
  fieldRef: string;
  fieldLabel: string;
  fieldType: FieldType | ParticipantFieldType;
  fieldValues?: ChoiceValue[];
  loadOnlyUsedValues?: boolean;
  loadOldValues?: boolean;
  currentFilterData?: FilterData;
  sessionId?: string;
  onFilterDataChange: (
    field: string,
    type: FilterType,
    values: string[],
    min?: number | Date,
    max?: number | Date,
  ) => void;
}

const isRangeFilterType = (type: FieldType | ParticipantFieldType): boolean => {
  return (
    type === ParticipantFieldType.DATE ||
    type === ParticipantFieldType.NUMBER ||
    type === ParticipantFieldType.INCOME
  );
};

const isChoiceFilterType = (
  type: FieldType | ParticipantFieldType,
): boolean => {
  return (
    type === FieldType.SINGLE_CHOICE ||
    type === FieldType.MULTIPLE_CHOICE ||
    type === ParticipantFieldType.SINGLE_CHOICE ||
    type === ParticipantFieldType.MULTIPLE_CHOICE ||
    type === ParticipantFieldType.TAGS ||
    type === ParticipantFieldType.LOCALES
  );
};

const isTextFilterType = (type: FieldType | ParticipantFieldType): boolean => {
  return (
    type === FieldType.SHORT_TEXT ||
    type === FieldType.LONG_TEXT ||
    type === ParticipantFieldType.SHORT_TEXT ||
    type === ParticipantFieldType.LONG_TEXT ||
    type === ParticipantFieldType.EMAIL
  );
};

export const Filter = ({
  fieldRef,
  fieldLabel,
  fieldType,
  fieldValues,
  currentFilterData,
  sessionId,
  onFilterDataChange,
  loadOnlyUsedValues,
  loadOldValues,
}: Props) => {
  if (isRangeFilterType(fieldType)) {
    return (
      <RangeFilterButton
        fieldRef={fieldRef}
        fieldLabel={fieldLabel}
        fieldType={fieldType}
        selectedMin={currentFilterData?.min}
        selectedMax={currentFilterData?.max}
        onFilterDataChange={onFilterDataChange}
      />
    );
  }

  if (isChoiceFilterType(fieldType)) {
    return (
      <FieldFilterButton
        fieldRef={fieldRef}
        fieldLabel={fieldLabel}
        fieldType={fieldType}
        values={fieldValues}
        selectedValues={currentFilterData?.values || []}
        sessionId={sessionId}
        onFilterDataChange={onFilterDataChange}
        textStyle={fieldType === ParticipantFieldType.TAGS ? 'bold' : 'normal'}
        loadOnlyUsedValues={loadOnlyUsedValues}
        loadOldValues={loadOldValues}
      />
    );
  }

  if (isTextFilterType(fieldType)) {
    return (
      <TextSearchFilterButton
        fieldRef={fieldRef}
        fieldLabel={fieldLabel}
        fieldType={fieldType}
        inputValue={currentFilterData?.values[0] || undefined}
        onFilterDataChange={onFilterDataChange}
      />
    );
  }

  return null;
};
